// /////////////////////////////////////////////////////////////
// [ファイル] useAssistantGroupOrder.ts
// [概要] AI アシスタントのグループ並び順を「利用者ごと」に Firestore
//        (HozenallCloud_Assistant_UserPrefs/{email}) へ保持するフック。
//        グループは共有されるため全員共通の順にせず、各自が自分の並びを持つ
//        (メンバー同士のドラッグが互いの並びを崩し合わないようにする)。
//        並び順に無いグループ (新規作成・未設定) は末尾に名前順で付き、
//        削除されたグループの id は並び替え時に自然に落ちる。
//        一覧で閉じているグループ (collapsed_groups、'' は共通) も同じ doc に持ち、
//        再読み込み・別端末でも開閉状態を再現する。新しいグループは記録が無いので開いて表示される。
// /////////////////////////////////////////////////////////////
import { useCallback, useEffect, useRef, useState } from 'react';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { useAuth } from '../firebase/AuthProvider';

const COLLECTION = 'HozenallCloud_Assistant_UserPrefs';

interface Orderable {
  id: string;
  name: string;
}

export function useAssistantGroupOrder() {
  const { user } = useAuth();
  const email = user?.email ?? null;
  const [groupOrder, setGroupOrder] = useState<string[]>([]);
  // 一覧で閉じているグループ id ('' は共通)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());
  const collapsedRef = useRef<Set<string>>(new Set());
  // 表示設定 (並び順・開閉) を 1 回でも読み終えたか。読めるまで一覧を出さない (後から並び替わるのを防ぐ)
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  useEffect(() => {
    collapsedRef.current = collapsedGroups;
  }, [collapsedGroups]);

  useEffect(() => {
    if (!email) {
      setGroupOrder([]);
      setCollapsedGroups(new Set());
      return;
    }
    const unsub = onSnapshot(
      doc(db, COLLECTION, email),
      (snap) => {
        const order = snap.data()?.group_order;
        setGroupOrder(Array.isArray(order) ? order.filter((v): v is string => typeof v === 'string') : []);
        const collapsed = snap.data()?.collapsed_groups;
        setCollapsedGroups(
          new Set(Array.isArray(collapsed) ? collapsed.filter((v): v is string => typeof v === 'string') : [])
        );
        setPrefsLoaded(true);
      },
      (err) => {
        // 読めなくても一覧は名前順で出せるため、会話自体は妨げない
        console.error('グループ並び順の購読に失敗しました', err);
        setPrefsLoaded(true);
      }
    );
    return () => unsub();
  }, [email]);

  /** 保存済みの並び順でグループを並べる (未登録は末尾に名前順) */
  const sortGroups = useCallback(
    <T extends Orderable>(groups: T[]): T[] => {
      const rank = new Map(groupOrder.map((id, i) => [id, i]));
      return [...groups].sort((a, b) => {
        const ra = rank.get(a.id);
        const rb = rank.get(b.id);
        if (ra !== undefined && rb !== undefined) return ra - rb;
        if (ra !== undefined) return -1;
        if (rb !== undefined) return 1;
        return a.name.localeCompare(b.name, 'ja');
      });
    },
    [groupOrder]
  );

  /** 並び順を保存する (ドラッグ確定時。全グループ id の配列を渡す) */
  const saveGroupOrder = useCallback(
    async (ids: string[]) => {
      if (!email) return;
      // 購読の反映を待たずに即時反映する (ドロップ直後の跳ね戻りを防ぐ)
      setGroupOrder(ids);
      await setDoc(
        doc(db, COLLECTION, email),
        { user_id: email, group_order: ids, updated_at: serverTimestamp() },
        { merge: true }
      );
    },
    [email]
  );

  /**
   * グループの開閉を切り替えて保存する。knownIds = 今ある全グループ id ('' = 共通を含む)。
   * 保存時に削除済みグループの記録を落とす。画面は即時に切り替え、保存の失敗は呼び元へ投げる
   */
  const toggleGroupCollapsed = useCallback(
    async (gid: string, knownIds: string[]) => {
      if (!email) return;
      const known = new Set(knownIds);
      // 現在値は ref から取る (setState の更新関数は遅れて実行されるため、保存する値をここで確定させる)
      const s = new Set(collapsedRef.current);
      if (s.has(gid)) s.delete(gid);
      else s.add(gid);
      const next = [...s].filter((id) => known.has(id));
      collapsedRef.current = new Set(next);
      setCollapsedGroups(new Set(next));
      await setDoc(
        doc(db, COLLECTION, email),
        { user_id: email, collapsed_groups: next, updated_at: serverTimestamp() },
        { merge: true }
      );
    },
    [email]
  );

  return { groupOrder, sortGroups, saveGroupOrder, collapsedGroups, toggleGroupCollapsed, prefsLoaded };
}
